/**
 * Utilidades para fondos personalizados por edificio (FIN-019).
 *
 * Cada edificio puede tener fondos custom (ej: "Fondo Calefaccion", "Fondo Obra Fachada")
 * ademas de los 2 fondos built-in: Caja (gastos comunes) y Reserva.
 */

// IDs reservados que no se pueden usar para fondos custom
export const RESERVED_FUND_IDS = ["caja", "reserva", "obras"];

// Paleta de colores para fondos personalizados
export const CUSTOM_FUND_COLORS = [
  "#e67e22", // naranja
  "#9b59b6", // violeta
  "#1abc9c", // turquesa
  "#e74c3c", // rojo
  "#2ecc71", // verde
  "#3498db", // azul claro
  "#f39c12", // amarillo oscuro
  "#8e44ad", // purpura
  "#16a085", // teal
  "#d35400", // naranja oscuro
];

/**
 * Genera un slug a partir del nombre del fondo.
 * Ej: "Fondo Calefacción" -> "calefaccion"
 *     "Obra Fachada Norte" -> "obra_fachada_norte"
 */
export function slugifyFundName(name) {
  return (name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remover acentos
    .toLowerCase()
    .replace(/^fondo\s+/i, "") // remover prefijo "fondo "
    .trim()
    .replace(/[^a-z0-9]+/g, "_") // reemplazar caracteres especiales por _
    .replace(/^_|_$/g, ""); // remover _ al inicio/final
}

/**
 * Genera la key del breakdown para un fondo custom.
 * Ej: "calefaccion" -> "fondo_calefaccion"
 */
export function fundBreakdownKey(fundId) {
  return `fondo_${fundId}`;
}

/**
 * Retorna todos los fondos activos de un edificio (built-in + custom).
 * @param {Object} building - documento del edificio
 * @returns {Array<{id, name, type, color}>}
 */
export function getAllActiveFunds(building) {
  const funds = [
    { id: "caja", name: "Caja", type: "builtin", color: "#0080ff" },
    { id: "reserva", name: "Reserva", type: "builtin", color: "#8b5cf6" },
  ];
  (building?.customFunds || [])
    .filter((f) => f.active !== false)
    .forEach((f, i) => {
      funds.push({
        id: f.id,
        name: f.name,
        type: "custom",
        color: f.color || CUSTOM_FUND_COLORS[i % CUSTOM_FUND_COLORS.length],
        method: f.method,
        percentage: f.percentage,
        fixedAmount: f.fixedAmount,
        initialBalance: f.initialBalance,
        initialBalanceUSD: f.initialBalanceUSD,
      });
    });
  return funds;
}

/**
 * Nombre amigable para una key de breakdown.
 * Ej: "gastos_comunes" -> "Gastos Comunes"
 *     "fondo_reserva" -> "Fondo de Reserva"
 *     "fondo_calefaccion" -> busca en customFunds del building, o capitaliza
 */
export function friendlyBreakdownName(key, building) {
  const builtinNames = {
    gastos_comunes: "Gastos Comunes",
    fondo_reserva: "Fondo de Reserva",
    multas: "Multas",
    intereses: "Recargo por mora",
    reajuste: "Reajuste (UI)",
    obras: "Obras",
    alquiler: "Alquiler", // unidad "Alquiler del edificio" (ingreso del fondo del contrato)
  };
  if (builtinNames[key]) return builtinNames[key];

  // Multas desglosadas: multa__Razon__userid_0
  if (key.startsWith("multa__")) {
    const withoutPrefix = key.replace("multa__", "");
    // Extraer razón (primera parte antes del __userid)
    const reasonPart = withoutPrefix.split("__")[0] || "";
    const reason = reasonPart.replace(/_/g, " ");
    return `Multa: ${reason}`;
  }

  // Cargos puntuales desglosados: cargo__Concepto__userid_0
  if (key.startsWith("cargo__")) {
    const withoutPrefix = key.replace("cargo__", "");
    const conceptPart = withoutPrefix.split("__")[0] || "";
    const concept = conceptPart.replace(/_/g, " ");
    return `Cargo: ${concept}`;
  }

  // Amenities desglosados: amenity__Barbacoa__2026-04-17__userid_0
  if (key.startsWith("amenity__")) {
    const withoutPrefix = key.replace("amenity__", "");
    const parts = withoutPrefix.split("__");
    const amenityName = (parts[0] || "").replace(/_/g, " ");
    const date = parts[1] || "";
    return date ? `${amenityName} (${date})` : amenityName;
  }

  // Buscar en customFunds del building
  if (key.startsWith("fondo_") && building?.customFunds) {
    const fundId = key.replace("fondo_", "");
    const fund = building.customFunds.find((f) => f.id === fundId);
    if (fund) return fund.name;
  }

  // Fallback: capitalizar
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Cuenta bancaria (UYU) asociada a un fondo del edificio.
 * caja → building.cajaBankAccountId; reserva → building.reserveFundBankAccountId;
 * custom → customFunds[].bankAccountId. Retorna "" si el fondo no tiene cuenta asignada.
 */
export function getFundBankAccountId(building, fundId) {
  if (!building) return "";
  if (fundId === "caja") return building.cajaBankAccountId || "";
  if (fundId === "reserva") return building.reserveFundBankAccountId || "";
  const cf = (building.customFunds || []).find((f) => f.id === fundId);
  return cf?.bankAccountId || "";
}

/**
 * Agrupa los fondos contables del edificio por la cuenta bancaria (UYU) que
 * comparten. Sirve para la vista de "saldo bancario consolidado": cuando caja y
 * reserva (o cualquier fondo) apuntan a la MISMA cuenta, el saldo físico de esa
 * cuenta es la suma de los saldos contables de todos los fondos que la usan.
 *
 * La segregación legal se mantiene contable (cada fondo conserva su saldo), aunque
 * la cuenta física sea una sola.
 *
 * @param {Object} building
 * @returns {{
 *   groups: Array<{ accountId: string, account: Object|null, funds: Array<{id,name,type,color}>, shared: boolean }>,
 *   unassigned: Array<{id,name,type,color}>,
 *   hasSharedAccount: boolean
 * }}
 */
export function groupFundsByBankAccount(building) {
  const funds = getAllActiveFunds(building);
  const accounts = Array.isArray(building?.bankAccounts) ? building.bankAccounts : [];
  const byAccount = new Map();
  const unassigned = [];

  funds.forEach((fund) => {
    const accId = getFundBankAccountId(building, fund.id);
    if (!accId) { unassigned.push(fund); return; }
    if (!byAccount.has(accId)) byAccount.set(accId, []);
    byAccount.get(accId).push(fund);
  });

  const groups = Array.from(byAccount.entries()).map(([accountId, groupFunds]) => ({
    accountId,
    account: accounts.find((a) => a.id === accountId) || null,
    funds: groupFunds,
    shared: groupFunds.length > 1,
  }));

  return {
    groups,
    unassigned,
    hasSharedAccount: groups.some((g) => g.shared),
  };
}
